/* Lionheart — news feeds
   Every element with [data-feed] is filled from assets/data/posts.json, so one new post
   updates the News pages, the homepage and any related page at once.

   Attributes:
     data-feed      layout: cards | tiles | feature | quotes | press | videos | jobs
     data-blog      blog key(s), comma-separated: updates, blog, in-the-news, videos, careers (default: all)
     data-tags      only posts with at least one of these tags, comma-separated
     data-tags-all  only posts that have every one of these tags, comma-separated (e.g. "recipe,coconut-aminos")
     data-limit     number of posts to show (default 6)
     data-skip      skip the first N matching posts (e.g. after a feature)
     data-more      "true" adds a Load More button (pages of data-limit)
     data-filters   "true" adds tag filter chips above the feed
     data-exclude   leave out the post with this URL (e.g. the role being viewed)
   A feed inside an element marked [data-feed-section] (usually starting out hidden) shows that
   element only when the feed has posts, so a section with nothing to show stays out of sight.
   Any markup already inside the element is kept as a fallback if the data can't load. */
(function () {
  "use strict";
  const feeds = document.querySelectorAll("[data-feed]");
  if (!feeds.length) return;

  const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const fmtDate = (d) => {
    if (!d) return "";
    const dt = new Date(d + "T00:00:00");
    return isNaN(dt) ? "" : dt.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  };
  const isExternal = (url) => /^https?:\/\//.test(url || "") && !/lionheartfarms\.com\.ph/.test(url);
  const linkAttrs = (url) => (isExternal(url) ? ' target="_blank" rel="noopener"' : "");
  const arrow = '<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 10h12M11 5l5 5-5 5"/></svg>';
  const play = '<span><svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg></span>';

  /* Tags typed in Shopify ("Food Safety") match feed tags ("food-safety") */
  const norm = (t) => String(t).trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  let fallbackImg = "assets/img/farm-aerial.jpg";

  /* On Shopify the theme embeds the posts as JSON (#lh-posts); the static site loads the data file */
  let data = null;
  const load = () => data || (data = (() => {
    const embedded = document.getElementById("lh-posts");
    if (embedded) return Promise.resolve(JSON.parse(embedded.textContent));
    return fetch(new URL("assets/data/posts.json", document.baseURI)).then((r) => {
      if (!r.ok) throw new Error(r.status);
      return r.json();
    });
  })().then((json) => {
    if (json.fallbackImage) fallbackImg = json.fallbackImage;
    (json.posts || []).forEach((p) => { p.tags = (p.tags || []).map(norm); });
    return json;
  }));

  const select = (all, el) => {
    const blogs = (el.dataset.blog || "").split(",").map((s) => s.trim()).filter(Boolean);
    const tags = (el.dataset.tags || "").split(",").map((s) => s.trim()).filter(Boolean);
    const tagsAll = (el.dataset.tagsAll || "").split(",").map((s) => norm(s)).filter(Boolean);
    return all
      .filter((p) => !blogs.length || blogs.includes(p.blog))
      .filter((p) => !tags.length || (p.tags || []).some((t) => tags.includes(t)))
      .filter((p) => tagsAll.every((t) => (p.tags || []).includes(t)))
      .filter((p) => !el.dataset.exclude || p.url !== el.dataset.exclude)
      .sort((a, b) => (b.date || "0000").localeCompare(a.date || "0000"));
  };

  const meta = (p, blogsMeta) => {
    const bits = [];
    if (p.source) bits.push(esc(p.source));
    else if (blogsMeta[p.blog]) bits.push(esc(blogsMeta[p.blog].title));
    if (p.date) bits.push(esc(fmtDate(p.date)));
    return bits.join(" · ");
  };
  const placeholder = (p) => (p.placeholder ? `<span class="ph-inline feed-ph">${esc(p.placeholder)}</span>` : "");
  const imgTag = (p) => `<img src="${esc(p.image || fallbackImg)}" alt="" loading="lazy" onerror="this.onerror=null;this.src='${esc(fallbackImg)}'">`;

  const render = {
    cards: (p, m) => `
      <article class="card news-card">
        ${p.url ? `<a class="card__media" href="${esc(p.url)}"${linkAttrs(p.url)} tabindex="-1" aria-hidden="true">${imgTag(p)}</a>` : `<div class="card__media">${imgTag(p)}</div>`}
        <div class="card__body">
          <time>${meta(p, m)}</time>
          <h3>${p.url ? `<a href="${esc(p.url)}"${linkAttrs(p.url)}>${esc(p.title)}</a>` : esc(p.title)}</h3>
          ${p.excerpt ? `<p>${esc(p.excerpt)}</p>` : ""}
          ${p.url ? `<a class="link-arrow" href="${esc(p.url)}"${linkAttrs(p.url)}>Read more</a>` : placeholder(p)}
        </div>
      </article>`,
    tiles: (p, m) => `
      <a class="news-tile" href="${esc(p.url || "#")}"${linkAttrs(p.url)}>${imgTag(p)}<div><h3>${esc(p.title)}</h3><small>${meta(p, m)}</small></div></a>`,
    feature: (p, m) => `
      <article class="news-feature news-card">
        <div class="card__media">${imgTag(p)}</div>
        <div class="card__body">
          <time>${meta(p, m)} · Latest</time>
          <h3><a href="${esc(p.url || "#")}"${linkAttrs(p.url)}>${esc(p.title)}</a></h3>
          ${p.excerpt ? `<p>${esc(p.excerpt)}</p>` : ""}
          <a class="link-arrow" href="${esc(p.url || "#")}"${linkAttrs(p.url)}>Read the story</a>
        </div>
      </article>`,
    quotes: (p) => `
      <figure class="quote">
        <blockquote>${p.quote ? `“${esc(p.quote)}”` : esc(p.excerpt || p.title)}</blockquote>
        <figcaption><cite>${esc(p.author || p.source || "")}${p.author && p.source ? ` <span>${esc(p.source)}</span>` : ""}</cite>
        ${p.url ? `<a class="link-arrow" href="${esc(p.url)}"${linkAttrs(p.url)}>Read the article</a>` : placeholder(p)}</figcaption>
      </figure>`,
    press: (p) => `
      <li class="press-item">
        <div class="press-item__source">${esc(p.source || "[Publication]")}</div>
        <div>
          <h3>${p.url ? `<a href="${esc(p.url)}"${linkAttrs(p.url)}>${esc(p.title)}</a>` : esc(p.title)}</h3>
          ${p.quote ? `<p>“${esc(p.quote)}”</p>` : p.excerpt ? `<p>${esc(p.excerpt)}</p>` : ""}
          <small>${[p.author, fmtDate(p.date)].filter(Boolean).map(esc).join(" · ")}</small>
          ${p.url ? "" : placeholder(p)}
        </div>
      </li>`,
    jobs: (p, m, tags) => `
      <a class="job" href="${esc(p.url || "#")}">
        <div><strong>${esc(p.title)}</strong><small>${[tags[(p.tags || [])[0]], p.location].filter(Boolean).map(esc).join(" · ")}</small></div>
        <span class="arrow" aria-hidden="true">→</span>
      </a>`,
    videos: (p) => `
      <div class="video-card">
        <div class="video-feature" ${p.youtube ? `data-youtube="${esc(p.youtube)}"` : "data-video"} data-title="${esc(p.title)}">
          ${p.youtube ? `<img src="${esc(p.image || `https://i.ytimg.com/vi/${p.youtube}/hqdefault.jpg`)}" alt="" loading="lazy">` : `<video preload="metadata" playsinline src="${esc(p.video)}#t=0.5"></video>`}
          <button class="video-play" aria-label="Play video: ${esc(p.title)}">${play}</button>
        </div>
        <h3>${esc(p.title)}</h3>
        ${p.excerpt ? `<p>${esc(p.excerpt)}</p>` : ""}
      </div>`,
  };

  const build = (el, all, json) => {
    const layout = el.dataset.feed in render ? el.dataset.feed : "cards";
    const limit = parseInt(el.dataset.limit, 10) || 6;
    const skip = parseInt(el.dataset.skip, 10) || 0;
    const posts = select(all, el).slice(skip);
    let shown = limit, activeTag = "";
    const section = el.closest("[data-feed-section]");
    if (section) section.hidden = posts.length === 0;

    const list = document.createElement(layout === "press" ? "ul" : "div");
    list.className = el.dataset.listClass || ({ cards: "grid grid-3", tiles: "news-tiles", feature: "", quotes: "quotes", press: "press-list", videos: "grid grid-2", jobs: "jobs" }[layout]);
    const more = document.createElement("button");
    more.type = "button";
    more.className = "btn btn--ghost feed-more";
    more.innerHTML = `Load More ${arrow}`;

    const filtered = () => (activeTag ? posts.filter((p) => (p.tags || []).includes(activeTag)) : posts);
    const draw = () => {
      const items = filtered();
      list.innerHTML = items.slice(0, shown).map((p) => render[layout](p, json.blogs || {}, json.tags || {})).join("") ||
        `<p class="feed-empty">${esc(el.dataset.empty || "No posts yet.")}</p>`;
      more.hidden = el.dataset.more !== "true" || shown >= items.length;
      if (window.lionheartInitVideos) window.lionheartInitVideos(list);
    };

    el.innerHTML = "";
    if (el.dataset.filters === "true") {
      const used = [...new Set(posts.flatMap((p) => p.tags || []))].filter((t) => json.tags && json.tags[t]);
      const bar = document.createElement("div");
      bar.className = "feed-filters";
      bar.setAttribute("role", "group");
      bar.setAttribute("aria-label", "Filter by topic");
      bar.innerHTML = [`<button type="button" aria-pressed="true" data-tag="">All</button>`]
        .concat(used.map((t) => `<button type="button" aria-pressed="false" data-tag="${esc(t)}">${esc(json.tags[t])}</button>`)).join("");
      bar.addEventListener("click", (e) => {
        const b = e.target.closest("button");
        if (!b) return;
        activeTag = b.dataset.tag;
        shown = limit;
        bar.querySelectorAll("button").forEach((x) => x.setAttribute("aria-pressed", String(x === b)));
        draw();
      });
      el.appendChild(bar);
    }
    el.appendChild(list);
    el.appendChild(more);
    more.addEventListener("click", () => { shown += limit; draw(); });
    draw();
    el.classList.add("is-visible");
  };

  load()
    .then((json) => feeds.forEach((el) => build(el, json.posts || [], json)))
    .catch(() => { /* keep the fallback markup already in the page */ });
})();
